import {
	CalendarDaysIcon as CalendarDaysIconOutline,
	PlayCircleIcon as PlayCircleIconOutline,
	TrophyIcon as TrophyIconOutline,
	UsersIcon as UsersIconOutline,
} from "@heroicons/react/24/outline";
import {
	CalendarDaysIcon,
	PlayCircleIcon,
	TrophyIcon,
	UsersIcon,
} from "@heroicons/react/24/solid";
import { useNotifications } from "@novu/react";
import {
	Link,
	type LinkProps,
	MatchRoute,
	useMatchRoute,
	useNavigate,
	useRouteContext,
	useRouter,
} from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { LayoutGroup, motion } from "motion/react";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { Popover } from "radix-ui";
import { Suspense, lazy, useId, useMemo, useState } from "react";
import { commitLocalUpdate, graphql, useFragment } from "react-relay";
import { Icon } from "@/components/icon";
import { Image } from "@/components/image";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
const AiDialog = lazy(() =>
	import("@/dialogs/ai-dialog").then((m) => ({ default: m.AiDialog })),
);
const ProfileUpdateDialog = lazy(() =>
	import("@/dialogs/profile-update-dialog").then((m) => ({
		default: m.ProfileUpdateDialog,
	})),
);
import { logout as logoutHandler } from "@/handlers/logout";
import { environment } from "@/relay/environment";
import { cn } from "@/utils/cn";
import { NovuProvider } from "@/utils/novu-provider";
import type { topnavFragment$key } from "./__generated__/topnavFragment.graphql";
import type { topnavRightPanel_user$key } from "./__generated__/topnavRightPanel_user.graphql";
import { CommunitySidebarContent } from "./community-sidebar";
import { NotificationWindow } from "./notification-window";

const icons = {
	selected: {
		CirclePlay: PlayCircleIcon,
		UsersRound: UsersIcon,
		CalendarDays: CalendarDaysIcon,
		CircleStar: TrophyIcon,
	},
	unselected: {
		CirclePlay: PlayCircleIconOutline,
		UsersRound: UsersIconOutline,
		CalendarDays: CalendarDaysIconOutline,
		CircleStar: TrophyIconOutline,
	},
};

function TopNavItem({
	current,
	title,
	to,
	icon,
}: {
	current: boolean;
	title: string;
	to: LinkProps["to"];
	icon: "CirclePlay" | "UsersRound" | "CalendarDays" | "CircleStar";
}) {
	const [isHovered, setIsHovered] = useState(false);

	const ItemIcon = icons[current ? "selected" : "unselected"][icon]!;

	return (
		<li
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			className="pr-2 last:pr-0"
		>
			<Link
				to={to}
				className={cn(
					"flex relative group text-sm flex-row items-center justify-center gap-2 font-medium px-4 py-1 rounded-md",
					current
						? "text-primary-foreground bg-primary"
						: "hover:text-accent-foreground",
				)}
			>
				{isHovered && (
					<motion.div
						layoutId="current-indicator"
						className="absolute -z-1 inset-0 w-full h-full bg-accent rounded-md"
					/>
				)}
				<span
					className={cn(
						"z-2 flex flex-row items-center justify-center gap-2",
						!current && "group-hover:text-accent-foreground",
					)}
				>
					<ItemIcon className="size-4" />
					<span className="flex-1 line-clamp-1">{title}</span>
				</span>
			</Link>
		</li>
	);
}

export function NotificationWindowToggle() {
	const [notificationWindowOpen, setNotificationWindowOpen] = useState(false);
	const { hasMore } = useNotifications();
	const { notifications: unreadNotifications } = useNotifications({
		read: false,
		limit: 50,
	});

	const unreadNotificationCount = useMemo(() => {
		const unreadCount = unreadNotifications?.length;

		if (unreadNotifications) {
			return unreadCount ? unreadCount.toString() : undefined;
		}

		if (hasMore) {
			return `${unreadCount}+`;
		}

		return unreadCount;
	}, [unreadNotifications, hasMore]);

	return (
		<Popover.Root
			open={notificationWindowOpen}
			onOpenChange={setNotificationWindowOpen}
		>
			<Popover.Trigger asChild>
				<Button type="button" variant="ghost" className="relative">
					<Icon className="size-5" name="Bell" />

					{unreadNotificationCount ? (
						<div className="size-3.5 font-semibold flex items-center justify-center rounded-full bg-destructive absolute bottom-0 right-0 text-[8px] text-destructive-foreground">
							{unreadNotificationCount}
						</div>
					) : null}
				</Button>
			</Popover.Trigger>

			<NotificationWindow open={notificationWindowOpen} />
		</Popover.Root>
	);
}

export function TopNavRightPanel({
	hideAi,
	hideSearch,
	user,
}: {
	hideAi?: boolean;
	hideSearch?: boolean;
	user?: topnavRightPanel_user$key;
}) {
	const { track } = useAnalytics();
	const data = useFragment<topnavRightPanel_user$key>(
		graphql`
    fragment topnavRightPanel_user on User {
        photo {
          signedUrl
        }
        avatarFallback
        ...profileUpdateDialog_user
    }
  `,
		user,
	);

	const navigate = useNavigate();
	const { session } = useRouteContext({ from: "__root__" });

	const [profileDialogOpen, setProfileDialogOpen] = useState(false);
	const [aiDrawerOpen, setAiDrawerOpen] = useState(false);

	const router = useRouter();
	const logout = useServerFn(logoutHandler);

	return (
		<Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
			<div className="justify-end flex items-center">
				<ul>
					{session?.user ? (
						<div className="flex flex-row gap-1 lg:gap-3 items-center justify-end">
							{hideAi ? null : (
								<Drawer
									direction="right"
									open={aiDrawerOpen}
									onOpenChange={setAiDrawerOpen}
								>
									<DrawerTrigger asChild>
										<Button variant="ghost">
											<Icon className="size-5" name="Sparkles" />
										</Button>
									</DrawerTrigger>

									{aiDrawerOpen && (
										<Suspense fallback={null}>
											<AiDialog />
										</Suspense>
									)}
								</Drawer>
							)}

							{!hideSearch && (
								<Link to="/search">
									<Button variant="ghost">
										<Icon className="size-5" name="Search" />
									</Button>
								</Link>
							)}

							<NotificationWindowToggle />

							<DropdownMenu>
								<DropdownMenuTrigger>
									<Avatar>
										{data?.photo?.signedUrl ? (
											<AvatarImage src={data?.photo?.signedUrl} />
										) : null}
										<AvatarFallback>
											{data?.avatarFallback || "??"}
										</AvatarFallback>
									</Avatar>
								</DropdownMenuTrigger>

								<DropdownMenuContent>
									<DialogTrigger asChild>
										<DropdownMenuItem>
											<Icon data-slot="icon" name="UserRound" />
											Meu Perfil
										</DropdownMenuItem>
									</DialogTrigger>

									<DropdownMenuItem
										onClick={() => {
											if (confirm("Tem certeza que deseja fazer logout?")) {
												track(AnalyticsEvents.AUTH_LOGOUT, {});
												logout().then(async () => {
													commitLocalUpdate(environment, (store) =>
														store.invalidateStore(),
													);

													router.invalidate();

													navigate({
														reloadDocument: true,
													});
												});
											}
										}}
									>
										<Icon data-slot="icon" name="LogOut" />
										Fazer Logout
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					) : (
						<Link to="/login">
							<Button
								variant="ghost"
								onClick={() => {
									track(AnalyticsEvents.AUTH_LOGIN_INITIATED, {
										source: "topnav",
									});
								}}
							>
								Entrar
							</Button>
						</Link>
					)}
				</ul>
			</div>

			{user && data && profileDialogOpen && (
				<Suspense fallback={null}>
					<ProfileUpdateDialog
						user={data}
						onRequestClose={() => setProfileDialogOpen(false)}
					/>
				</Suspense>
			)}
		</Dialog>
	);
}

export function TopNav({
	club: clubFragment,
	user,
}: {
	club: topnavFragment$key;
	user?: topnavRightPanel_user$key;
}) {
	const id = useId();
	const { club } = useRouteContext({ from: "__root__" });
	const data = useFragment<topnavFragment$key>(
		graphql`
    fragment topnavFragment on Club {
      aiPersona {
        enabled
      }

      showPoweredLogo

      ...communitySidebarFragment

      coursePageTitle: configuration(key: "COURSE_PAGE_TITLE")
      communityPageTitle: configuration(key: "COMMUNITY_PAGE_TITLE")
      eventsPageTitle: configuration(key: "EVENTS_PAGE_TITLE")
      leaderboardPageTitle: configuration(key: "LEADERBOARD_PAGE_TITLE")

			communityEnabled: configuration(key: "COMMUNITY_ENABLED")
			coursesEnabled: configuration(key: "COURSES_ENABLED")
			eventsEnabled: configuration(key: "EVENTS_ENABLED")
			rankingEnabled: configuration(key: "LEADERBOARD")

			events(orderBy: CHRONOLOGICAL) {
				edges {
					node {
						id
					}
				}
			}
			groups {
				spaces {
					isMember
					visibility
				}
			}
			coursePage {
				id
				categories(status: ACTIVE) {
					courses(status: ACTIVE) {
						visibility
						canView
					}
				}
			}
    }
  `,
		clubFragment,
	);

	const matchRoute = useMatchRoute();

	const hasEvents = data.events.edges.filter((n) => Boolean(n.node)).length;
	const hasCommunity = data.groups
		.filter(Boolean)
		.some((g) =>
			g.spaces
				.filter(Boolean)
				.some((s) => s.isMember || s.visibility === "PUBLIC"),
		);
	const hasCourses = data.coursePage.categories.some((c) =>
		c.courses.some(
			(c) =>
				c.visibility === "PRIVATE" ||
				c.visibility === "PUBLIC" ||
				c.visibility === "MEMBER_ONLY" ||
				c.canView,
		),
	);

	const isAdmin =
		club.permissions?.some((p) => p.slug === "CLUB_ADMIN") || false;
	const isModerator =
		club.permissions?.some((p) => p.slug === "MODERATOR") || false;

	const isCommunityPage =
		Boolean(
			matchRoute({ to: "/comunidade" }) ||
				matchRoute({ to: "/comunidade/s/$space" }) ||
				matchRoute({ to: "/comunidade/s/$space/posts/$post" }) ||
				matchRoute({ to: "/posts/novo" }),
		) || false;

	const isEvents =
		Boolean(
			matchRoute({ to: "/eventos" }) ||
				matchRoute({ to: "/eventos/$slug" }) ||
				matchRoute({ to: "/eventos/novo" }) ||
				matchRoute({ to: "/eventos/$slug/editar" }),
		) || false;

	const isRanking =
		Boolean(
			matchRoute({ to: "/ranking/leaderboard" }) ||
				matchRoute({ to: "/ranking/pontuacao" }),
		) || false;

	const isCoursesEnabled =
		typeof data.coursesEnabled !== "string" || data.coursesEnabled === "1";
	const isCommunityEnabled =
		typeof data.communityEnabled !== "string" || data.communityEnabled === "1";
	const isEventsEnabled =
		typeof data.eventsEnabled !== "string" || data.eventsEnabled === "1";
	const isLeaderboardEnabled =
		typeof data.rankingEnabled !== "string" ||
		data.rankingEnabled === "1" ||
		data.rankingEnabled === "ENABLED";

	return (
		<div
			className={cn(
				"grid max-md:h-[72px] h-[60px] md:min-h-[60px] md:max-h-[60px] border-0 border-b z-2 w-full grid-cols-4 max-lg:grid-cols-2 px-6 bg-background backdrop-blur-md text-foreground",
			)}
		>
			<div className="flex flex-row items-center justify-start">
				{isCommunityPage && (
					<Drawer direction="left">
						<DrawerTrigger asChild>
							<Button size="icon" variant="ghost" className="lg:hidden mr-3">
								<Icon name="Menu" />
							</Button>
						</DrawerTrigger>

						<DrawerContent className="overflow-y-auto">
							<CommunitySidebarContent club={data} />
						</DrawerContent>
					</Drawer>
				)}

				<Link
					to="/"
					className="flex flex-row items-center justify-start mr-3 gap-1.5"
				>
					{club?.logo ? (
						<Avatar>
							<AvatarImage
								src={club.logo?.signedUrl}
								alt={`${club.name} logo`}
							/>
							<AvatarFallback>
								{club.name.slice(0, 2).toUpperCase()}
							</AvatarFallback>
						</Avatar>
					) : null}

					<span className="line-clamp-1">{club?.name}</span>
				</Link>

				{Boolean(data) && Boolean(data.showPoweredLogo) && (
					<Link
						to="https://scarf.club"
						target="_blank"
						className="hidden rounded-md border bg-muted px-3 py-1.5 text-muted-foreground text-xs lg:block"
					>
						<span className="flex items-center leading-none">
							Powered by{" "}
							<Image
								src={
									"https://storage.googleapis.com/scarf-club-assets/logos/Scarf-Full-Primary.svg"
								}
								className="ml-1.5 inline h-4"
							/>
						</span>
					</Link>
				)}
			</div>

			<LayoutGroup id={id}>
				<ul className="flex flex-row items-center col-span-2 gap-0 justify-center w-full max-lg:hidden text-sidebar-foreground">
					{isAdmin || (hasCourses && isCoursesEnabled) ? (
						<MatchRoute to="/cursos">
							{(match) => (
								<TopNavItem
									current={Boolean(match)}
									title={data.coursePageTitle || "Cursos"}
									to="/cursos"
									icon="CirclePlay"
								/>
							)}
						</MatchRoute>
					) : null}

					{isAdmin || isModerator || (hasCommunity && isCommunityEnabled) ? (
						<TopNavItem
							current={isCommunityPage}
							title={data.communityPageTitle || "Comunidade"}
							to="/comunidade"
							icon="UsersRound"
						/>
					) : null}

					{isAdmin || (hasEvents && isEventsEnabled) ? (
						<TopNavItem
							current={isEvents}
							title={data.eventsPageTitle || "Eventos"}
							to="/eventos"
							icon="CalendarDays"
						/>
					) : null}

					{isAdmin || isLeaderboardEnabled ? (
						<TopNavItem
							current={isRanking}
							title={data.leaderboardPageTitle || "Ranking"}
							to="/ranking/leaderboard"
							icon="CircleStar"
						/>
					) : null}
				</ul>
			</LayoutGroup>

			<NovuProvider>
				<TopNavRightPanel user={user} hideAi={!data.aiPersona?.enabled} />
			</NovuProvider>
		</div>
	);
}
